import { useTheme } from "@emotion/react";
import { Divider } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary";
import { practitionerRoutes } from "@practitioner/data/practitionerRoutes";
import { SvgFullscriptIcon, SvgFullscriptIconDarkGreen } from "@shared/assets/logo";
import FullscriptLogoGreenLeafPigeonTextSVGComponent from "@shared/assets/logo/FullscriptLogoGreenLeafPigeonText";
import FullscriptLogoWhiteTextGreenLeafSVGComponent from "@shared/assets/logo/FullscriptLogoWhiteTextGreenLeaf";
import { useFlippers } from "@shared/context/FlippersContext";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCopyToClipboard } from "@shared/hooks/useCopyToClipboard/useCopyToClipboard";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./StoreLandingPageNavBar.styles";

interface Props {
  isPreview?: boolean;
  isStoreOwner?: boolean;
  isShowOnDesktop?: boolean;
  isStorefronts?: boolean;
  iconLogo?: string;
}

const StoreLandingPageNavBar = ({
  isPreview,
  isStoreOwner,
  isShowOnDesktop,
  isStorefronts = false,
  iconLogo,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { pathname } = useLocation();
  const currentTheme = useTheme();
  const [isCoBrandingEnabled] = useFlippers("vanguard_storefronts_co_branding");
  const theme = useTheme();
  const currentPath = `${location.origin}${pathname}`;
  const copyToClipboard = useCopyToClipboard();

  const shouldShowCoBranding = isStorefronts && isCoBrandingEnabled && !isPreview;

  const copyEmbedCode = async () => {
    await copyToClipboard(currentPath, {
      successMessage: t(l.patientRxAuth.practiceLandingPages.toastCopySuccessMessage),
    });
  };

  const renderFsCobrandingLogo = () => {
    if (currentTheme.theme === "dark") {
      return <SvgFullscriptIcon />;
    }

    return <SvgFullscriptIconDarkGreen />;
  };

  const renderLogo = () => {
    if (shouldShowCoBranding) {
      return (
        <div css={styles.homeWithCoBranding}>
          {renderFsCobrandingLogo()}
          {!!iconLogo && (
            <>
              <Divider customCss={styles.divider(currentTheme)} />
              <img src={iconLogo} css={styles.coBrandingLogo} alt="" />
            </>
          )}
        </div>
      );
    }

    if (theme.theme === "dark") {
      return (
        <FullscriptLogoWhiteTextGreenLeafSVGComponent
          css={styles.svgContainer}
          data-testid="fullscript-logo-dark"
        />
      );
    }

    return (
      <FullscriptLogoGreenLeafPigeonTextSVGComponent
        css={styles.svgContainer}
        data-testid="fullscript-logo"
      />
    );
  };

  const previewPageNav = (
    <div css={styles.navBar} data-testid="storeSignupPreviewNav">
      {!!tablet.lessThan && renderLogo()}

      <div css={styles.buttonsContainer}>
        {!!isStoreOwner && (
          <Button
            href={practitionerRoutes.storeSettings.landingPage}
            css={styles.button}
            size="small"
          >
            {t(l.patientRxAuth.practiceLandingPages.backToEdit)}
          </Button>
        )}
        <Button intention="secondary" css={styles.button} onClick={copyEmbedCode} size="small">
          {tablet.lessThan
            ? t(l.patientRxAuth.practiceLandingPages.copyLink)
            : t(l.patientRxAuth.practiceLandingPages.copyPracticeLink)}
        </Button>
      </div>
    </div>
  );

  const pageNav = <div css={styles.navBar}>{renderLogo()}</div>;

  return (
    <>
      {!!isPreview && previewPageNav}
      {!isPreview && !!(tablet.lessThan || isShowOnDesktop) && pageNav}
    </>
  );
};

export { StoreLandingPageNavBar };
